/** Edit shortcut-registry.json to change defaults for both native and web UI. */
import definitions from "./shortcut-registry.json";
import { isWindowsPlatform } from "./os-platform";
import { formatNumber, translate } from "@/i18n";
import { libShortcutsMessages } from "@/i18n/messages/lib-shortcuts";
import type en from "@/i18n/messages/lib-shortcuts/en";
import {
  SHORTCUT_LABEL_KEY,
  settingsShortcutsMessages,
} from "@/i18n/messages/settings-shortcuts";
import type { MessageArgs } from "@/i18n/types";

export type ShortcutPlatform = "macos" | "windows";
export type ShortcutId =
  | "shortcut.toggle_workspace"
  | "shortcut.input.open"
  | "shortcut.voice.toggle"
  | "shortcut.draw"
  | "shortcut.voice.continuous"
  | "shortcut.voice.new"
  | "shortcut.dictation.anywhere"
  | "shortcut.voice.cancel"
  | "shortcut.speech.stop"
  | "shortcut.workspace.peek"
  | "shortcut.frame.multiselect";
export type ShortcutBinding = {
  keys: string[];
  gesture: "chord" | "doubleTap" | "hold" | "drag" | "click";
  holdMs?: number;
  doubleTapMs?: number;
  holdKeys?: string[];
};
export type ShortcutDefinition = {
  id: ShortcutId;
  label: string;
  group: "Input" | "Workspace";
  primary: boolean;
  testId: string;
  scope: "global" | "drawing" | "workspace" | "voice" | "speech";
  context?: string;
  defaults: Record<ShortcutPlatform, ShortcutBinding>;
};
export const SHORTCUT_REGISTRY = definitions as ShortcutDefinition[];
const tr = <K extends keyof typeof en>(
  key: K,
  ...args: MessageArgs<(typeof en)[K]>
) => translate(libShortcutsMessages, key, ...args);
/** What a shortcut does, in the app language ("Open input bar"). */
export function shortcutActionLabel(id: ShortcutId): string {
  return translate(settingsShortcutsMessages, SHORTCUT_LABEL_KEY[id]);
}
export function shortcutPlatform(): ShortcutPlatform {
  return isWindowsPlatform() ? "windows" : "macos";
}
export function getDefaultBinding(
  id: ShortcutId,
  platform = shortcutPlatform(),
): ShortcutBinding {
  return SHORTCUT_REGISTRY.find((item) => item.id === id)!.defaults[platform];
}
export function shortcutKeyboardLabel(
  key: string,
  platform = shortcutPlatform(),
): string {
  const sidedModifier = /^(Control|Alt|Meta|Shift)(Left|Right)$/.test(key);
  const base = sidedModifier ? key.replace(/(Left|Right)$/, "") : key;
  const name =
    {
      Control: platform === "macos" ? "Control" : "Ctrl",
      Alt: platform === "macos" ? "Option" : "Alt",
      Meta: platform === "macos" ? "Command" : "Windows",
      Escape: "Escape",
      CapsLock: "Caps Lock",
      BracketLeft: "[",
      BracketRight: "]",
      ArrowLeft: "←",
      ArrowRight: "→",
      ArrowUp: "↑",
      ArrowDown: "↓",
      Backquote: "`",
      Backslash: "\\",
      Slash: "/",
      Quote: "'",
      Semicolon: ";",
      Comma: ",",
      Period: ".",
      Minus: "-",
      Equal: "=",
    }[base] ?? base.replace(/^Key/, "").replace(/^Digit/, "");
  if (!sidedModifier) return name;
  return tr(key.endsWith("Left") ? "keyLeft" : "keyRight", { key: name });
}
/** Keys by their keyboard names: "Control + Option", "Ctrl + Windows". */
export function shortcutKeyNames(
  keys: string[],
  platform = shortcutPlatform(),
): string {
  return keys.map((key) => shortcutKeyboardLabel(key, platform)).join(" + ");
}
export function describeShortcut(
  binding: ShortcutBinding,
  platform = shortcutPlatform(),
): string {
  const keys = shortcutKeyNames(binding.keys, platform);
  if (binding.gesture === "drag") return tr("holdDrag", { keys });
  if (binding.gesture === "click") return tr("click", { keys });
  if (binding.gesture === "doubleTap")
    return binding.holdKeys?.length
      ? tr("holdDoubleTap", {
          held: shortcutKeyNames(binding.holdKeys, platform),
          keys,
        })
      : tr("doubleTap", { keys });
  if (binding.gesture === "hold") {
    const seconds = (binding.holdMs ?? 2500) / 1000;
    return tr("holdFor", {
      keys,
      count: seconds,
      seconds: formatNumber(seconds),
    });
  }
  return keys;
}
export function bindingSignature(binding: ShortcutBinding): string {
  return JSON.stringify([
    binding.gesture,
    [...binding.keys].sort(),
    [...(binding.holdKeys ?? [])].sort(),
    binding.gesture === "hold" ? (binding.holdMs ?? 2500) : null,
    binding.gesture === "doubleTap" ? (binding.doubleTapMs ?? 450) : null,
  ]);
}
export function isShortcutModifier(key: string): boolean {
  return /^(Control|Alt|Meta|Shift)(Left|Right)?$/.test(key);
}
export function shortcutEventCode(
  event: Pick<KeyboardEvent, "key" | "code" | "location">,
): string {
  if (event.code) return event.code;
  if (isShortcutModifier(event.key)) {
    return event.location === 1
      ? `${event.key}Left`
      : event.location === 2
        ? `${event.key}Right`
        : event.key;
  }
  if (event.key === " ") return "Space";
  if (/^[a-z]$/i.test(event.key)) return `Key${event.key.toUpperCase()}`;
  if (/^\d$/.test(event.key)) return `Digit${event.key}`;
  return event.key;
}

export type ShortcutOverrides = Partial<Record<ShortcutId, ShortcutBinding>>;
function supportedKey(key: unknown): key is string {
  return (
    typeof key === "string" &&
    (isShortcutModifier(key) ||
      /^(Key[A-Z]|Digit[0-9]|F(?:[1-9]|1[0-2]))$/.test(key) ||
      [
        "Escape",
        "Space",
        "Enter",
        "Tab",
        "Backspace",
        "Delete",
        "ArrowUp",
        "ArrowDown",
        "ArrowLeft",
        "ArrowRight",
      ].includes(key))
  );
}
function keyFamily(key: string): string {
  return isShortcutModifier(key) ? key.replace(/(Left|Right)$/, "") : key;
}
/** Same keys, where a sideless modifier matches either side. */
export function keysOverlap(left: string[], right: string[]): boolean {
  return (
    left.length === right.length &&
    left.every((key) =>
      right.some(
        (other) =>
          keyFamily(key) === keyFamily(other) &&
          (key === other ||
            key === keyFamily(key) ||
            other === keyFamily(other)),
      ),
    )
  );
}
/**
 * Same supported gesture contract as native, also used for browser persistence.
 * A disabled shortcut keeps its own binding rules but takes no part in conflicts.
 */
export function validateShortcutOverrides(
  value: unknown,
  disabled: unknown = [],
): asserts value is ShortcutOverrides {
  if (
    !value ||
    typeof value !== "object" ||
    Array.isArray(value) ||
    !Array.isArray(disabled)
  )
    throw new Error(tr("savedUnreadable"));
  const unknown = disabled.find(
    (id) => !SHORTCUT_REGISTRY.some((item) => item.id === id),
  );
  if (unknown !== undefined) throw new Error(`Unknown shortcut: ${unknown}`);
  const overrides = value as ShortcutOverrides;
  for (const [id, raw] of Object.entries(overrides)) {
    const definition = SHORTCUT_REGISTRY.find((item) => item.id === id);
    if (!definition) throw new Error(`Unknown shortcut: ${id}`);
    if (
      !raw ||
      !Array.isArray(raw.keys) ||
      raw.keys.length < 1 ||
      raw.keys.length > 4 ||
      !raw.keys.every(supportedKey) ||
      (raw.holdKeys !== undefined &&
        (!Array.isArray(raw.holdKeys) ||
          raw.holdKeys.length > 3 ||
          !raw.holdKeys.every(supportedKey))) ||
      (raw.holdMs !== undefined &&
        (typeof raw.holdMs !== "number" || !Number.isFinite(raw.holdMs))) ||
      (raw.doubleTapMs !== undefined &&
        (!Number.isInteger(raw.doubleTapMs) ||
          raw.doubleTapMs < 100 ||
          raw.doubleTapMs > 2000))
    ) {
      throw new Error(tr("unsupported"));
    }
    const binding = raw as ShortcutBinding;
    const held = binding.holdKeys ?? [];
    const pointer = definition.defaults[shortcutPlatform()].gesture;
    if (
      (definition.scope === "global" &&
        !["chord", "doubleTap", "hold"].includes(binding.gesture)) ||
      (pointer === "drag" || pointer === "click"
        ? binding.gesture !== pointer
        : definition.scope !== "global" && binding.gesture !== "chord") ||
      !["chord", "doubleTap", "hold", "drag", "click"].includes(
        binding.gesture,
      ) ||
      binding.keys.filter((key) => !isShortcutModifier(key)).length > 1 ||
      !held.every(isShortcutModifier) ||
      ((binding.gesture === "drag" || binding.gesture === "click") &&
        !binding.keys.every(isShortcutModifier)) ||
      (held.length > 0 && binding.gesture !== "doubleTap") ||
      (binding.gesture === "doubleTap" && binding.keys.length !== 1) ||
      (binding.gesture === "hold" &&
        !(
          Number.isInteger(binding.holdMs) &&
          binding.holdMs! >= 250 &&
          binding.holdMs! <= 10000
        ))
    ) {
      throw new Error(tr("gestureNotAllowed"));
    }
    const keys = [...binding.keys, ...held];
    if (new Set(keys.map(keyFamily)).size !== keys.length)
      throw new Error(tr("oneSidePerModifier"));
  }
  const enabled = SHORTCUT_REGISTRY.filter(
    (item) => !disabled.includes(item.id),
  );
  for (let index = 0; index < enabled.length; index++) {
    const item = enabled[index]!;
    const binding = overrides[item.id] ?? getDefaultBinding(item.id);
    for (const other of enabled.slice(index + 1)) {
      const otherBinding = overrides[other.id] ?? getDefaultBinding(other.id);
      const pointerOverlap = (
        pointer: ShortcutBinding,
        action: ShortcutBinding,
        scope: string,
      ) =>
        ["drag", "click"].includes(pointer.gesture) &&
        scope === "global" &&
        ["chord", "hold"].includes(action.gesture) &&
        keysOverlap(pointer.keys, action.keys);
      if (
        pointerOverlap(binding, otherBinding, other.scope) ||
        pointerOverlap(otherBinding, binding, item.scope) ||
        ((item.scope === other.scope ||
          item.scope === "global" ||
          other.scope === "global" ||
          (binding.gesture === "drag" && otherBinding.gesture === "drag")) &&
          binding.gesture === otherBinding.gesture &&
          keysOverlap(binding.keys, otherBinding.keys) &&
          keysOverlap(binding.holdKeys ?? [], otherBinding.holdKeys ?? []))
      ) {
        throw new Error(
          tr("alreadyUsedBy", { action: shortcutActionLabel(other.id) }),
        );
      }
    }
  }
}

/** OS-owned Windows chords use modifier families; gestures retain physical sides. */
export function normalizeShortcutBinding(
  binding: ShortcutBinding,
): ShortcutBinding {
  if (binding.gesture !== "chord" || binding.keys.every(isShortcutModifier))
    return binding;
  return {
    ...binding,
    keys: binding.keys.map((key) =>
      isShortcutModifier(key) ? key.replace(/(Left|Right)$/, "") : key,
    ),
  };
}
